import React, { useEffect, useRef, useState } from 'react';
import { useStripe } from '@stripe/react-stripe-js';
import CardPaymentSection from './CardPaymentSection';
import { Sheet, PillButton, MUTED, SUBTLE, RED } from './club/clubUi';
import clubMembershipService from '../services/clubMembershipService';
import stripeService from '../services/stripeService';
import formatCurrency, { billingPeriodLabel } from '../utils/formatCurrency';

/**
 * The paid-join sheet (README §1 "Checkout"): JOIN · name · `$12.00 / month · CAD`; the grey
 * line-item box (Membership, Portal fee, GST, Processing, TOTAL); the yellow WALLET CAN COVER THIS
 * band; the black CHARGES … EVERY MONTH note; `PAY … FROM WALLET` / `OR PAY WITH CARD`, which
 * reveals the shared CardPaymentSection (Apple / Google Pay + card). Every number comes from
 * `clubMembershipService.getQuote`.
 */
export default function ClubMembershipCheckoutModal({ club, onClose, onSuccess, mobile = false }) {
  const stripe = useStripe();
  const cardRef = useRef(null);
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [cardReady, setCardReady] = useState(false);
  const [payCard, setPayCard] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const r = await clubMembershipService.getQuote(club.id);
      if (cancelled) return;
      if (r.success) setQuote(r.data);
      else setError(r.error || 'Could not load quote');
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [club.id]);

  const label = { fontSize: 10, fontWeight: 800, letterSpacing: '.14em', textTransform: 'uppercase' };

  if (loading) return <Sheet onClose={onClose} phone={mobile} width={500} testId="checkout-sheet"><span style={{ ...label, color: MUTED }}>LOADING…</span></Sheet>;
  if (!quote) return <Sheet onClose={onClose} phone={mobile} width={500} testId="checkout-sheet"><span style={{ fontSize: 12, color: RED }}>{error}</span></Sheet>;

  const cur = quote.currency;
  const walletCoversAll = quote.balancePortionCents > 0 && quote.cardPortionCents === 0;
  const usesWallet = quote.balancePortionCents > 0;
  const isSubscription = quote.mode === 'subscription';
  const showCard = payCard || !walletCoversAll;
  const cardTotal = quote.cardOnlyTotalCents || (walletCoversAll ? quote.totalCents : quote.cardPortionCents);
  const period = quote.billingPeriod === 'annual' ? 'year' : 'month';

  async function processPayment(prebuiltPaymentMethodId = null, skipBalance = false) {
    if (!stripe && quote.cardPortionCents > 0) return { ok: false, message: 'Stripe is not loaded' };
    let paymentMethodId = prebuiltPaymentMethodId;
    if ((quote.cardPortionCents > 0 || skipBalance) && !paymentMethodId) {
      const setupRes = await stripeService.createSetupIntent();
      if (!setupRes.success) return { ok: false, message: setupRes.error || 'Could not prepare payment' };
      const confirmed = await cardRef.current.confirmSetup(setupRes.data.clientSecret, {});
      if (confirmed.error) return { ok: false, message: confirmed.error };
      paymentMethodId = confirmed.paymentMethod;
    }
    const r = await clubMembershipService.checkout(club.id, { stripePaymentMethodId: paymentMethodId, skipBalance });
    return r.success ? { ok: true, data: r.data } : { ok: false, message: r.error || 'Checkout failed' };
  }

  const finish = (r) => { setSubmitting(false); if (r.ok) onSuccess?.(r.data); else setError(r.message); };
  async function payWallet() { setSubmitting(true); setError(null); finish(await processPayment(null, false)); }
  async function payCardNow(e) { e?.preventDefault?.(); setSubmitting(true); setError(null); finish(await processPayment(null, walletCoversAll)); }
  async function handleWalletPaymentMethod(pm, complete) {
    setError(null);
    const r = await processPayment(pm.id, true);
    complete(r.ok ? 'success' : 'fail');
    if (r.ok) onSuccess?.(r.data); else setError(r.message);
  }

  const lines = [
    ['Membership', quote.membershipPriceCents],
    quote.prServiceFeeCents > 0 ? ['Portal fee', quote.prServiceFeeCents] : null,
    quote.gstCents > 0 ? ['GST (5%)', quote.gstCents] : null,
    quote.stripeFeeCents > 0 ? ['Processing (2.9% + 30¢)', quote.stripeFeeCents] : null,
  ].filter(Boolean);
  const primaryLabel = submitting ? 'PROCESSING…' : showCard && (payCard || !walletCoversAll) ? `PAY ${formatCurrency(walletCoversAll ? cardTotal : quote.totalCents, cur)}${walletCoversAll ? ' WITH CARD' : ''}` : `PAY ${formatCurrency(quote.totalCents, cur)} FROM WALLET`;

  return (
    <Sheet onClose={onClose} phone={mobile} width={500} testId="checkout-sheet">
      <form onSubmit={payCardNow} className="flex flex-col" style={{ gap: 14 }}>
        <span className="flex items-start justify-between" style={{ gap: 10 }}>
          <span className="flex flex-col" style={{ gap: 6 }}>
            <span style={{ ...label, fontWeight: 700, letterSpacing: '.18em', color: MUTED }}>JOIN</span>
            <span style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.15 }}>{quote.clubName || club.name}</span>
            <span style={{ fontSize: 13 }}><b>{formatCurrency(quote.membershipPriceCents, cur)}</b> <span style={{ color: MUTED }}>{billingPeriodLabel(quote.mode, quote.billingPeriod, quote.termDays)} · {cur}</span></span>
          </span>
          <button type="button" onClick={onClose} aria-label="Close" style={{ flexShrink: 0, width: 40, height: 40, border: 'none', borderRadius: '50%', background: '#F2F2F2', fontFamily: 'inherit', fontSize: 14, cursor: 'pointer' }}>✕</button>
        </span>

        <div className="flex flex-col" style={{ gap: 8, padding: '16px 18px', borderRadius: 18, background: '#F5F5F5' }} data-testid="checkout-lines">
          {lines.map(([k, v]) => <span key={k} className="flex justify-between" style={{ gap: 12, fontSize: 12 }}><span style={{ color: MUTED }}>{k}</span><span style={{ fontWeight: 700 }}>{formatCurrency(v, cur)}</span></span>)}
          <span className="flex justify-between" style={{ gap: 12, paddingTop: 8, borderTop: '2px solid #000', fontSize: 15, fontWeight: 800 }}><span>TOTAL</span><span>{formatCurrency(quote.totalCents, cur)} {cur}</span></span>
        </div>

        {usesWallet && (
          <div className="flex items-center justify-between" style={{ gap: 10, padding: '12px 16px', borderRadius: 16, background: '#FEF3C7' }} data-testid="checkout-wallet">
            <span style={label}>{walletCoversAll ? 'WALLET CAN COVER THIS' : 'USING YOUR WALLET'} · {formatCurrency(quote.walletBalanceCents ?? quote.balancePortionCents, cur)}</span>
            <span style={{ fontSize: 14, fontWeight: 800 }}>−{formatCurrency(quote.balancePortionCents, cur)}</span>
          </div>
        )}

        {isSubscription && (
          <div className="flex flex-col" style={{ gap: 4, padding: '14px 16px', borderRadius: 16, background: '#000', color: '#fff' }}>
            <span style={label}>CHARGES {formatCurrency(quote.totalCents, cur)} {cur} EVERY {period.toUpperCase()}</span>
            <span style={{ fontSize: 11, lineHeight: 1.5, color: SUBTLE }}>Includes the Portal service fee and processing. Cancel any time and keep the {period} you paid for.</span>
          </div>
        )}

        {showCard && (
          <div className="flex flex-col" style={{ gap: 8 }} data-testid="checkout-card">
            <CardPaymentSection ref={cardRef} amountCents={cardTotal} currency={cur.toLowerCase()} countryDefault={cur === 'USD' ? 'US' : 'CA'} onValidityChange={setCardReady} onWalletPaymentMethod={handleWalletPaymentMethod} disabled={submitting} label={walletCoversAll ? 'OR PAY WITH CARD' : 'PAY WITH CARD'} />
          </div>
        )}

        {error && <span style={{ fontSize: 12, color: RED }} role="alert">{error}</span>}

        {walletCoversAll && !payCard ? (
          <>
            <PillButton size={54} fontSize={12} type="button" onClick={payWallet} disabled={submitting} data-testid="checkout-pay">{primaryLabel}</PillButton>
            <PillButton variant="secondary" size={46} fontSize={11} type="button" onClick={() => setPayCard(true)} data-testid="checkout-alt">OR PAY WITH CARD</PillButton>
          </>
        ) : (
          <>
            <PillButton size={54} fontSize={12} type="submit" disabled={submitting || !cardReady} style={{ opacity: cardReady ? 1 : 0.5 }} data-testid="checkout-pay">{primaryLabel}</PillButton>
            {walletCoversAll && <PillButton variant="secondary" size={46} fontSize={11} type="button" onClick={() => setPayCard(false)} data-testid="checkout-alt">USE MY WALLET INSTEAD</PillButton>}
          </>
        )}
      </form>
    </Sheet>
  );
}
